import { validateUserCredentials } from "../../../utils/userValidation";
import React, { useEffect, useState } from "react";
import { API } from "../../../services/api";
import { useAuth } from "../../../auth/AuthContext";
import { FieldLabel, PasswordConditions } from "../AdminCommon";
import { ROLES, ROLE_STYLES, avatarColor, userInitials } from "../adminConstants";
import {
    btnBlackStyle,
    btnOutlineDangerStyle,
    btnOutlineStyle,
    cardStyle,
    cardTitleStyle,
    countBadgeStyle,
    errorStyle,
    fieldStyle,
    inputStyle,
    tableStyle,
    tdStyle,
    thStyle,
} from "../styles/adminDashboardStyles";
import { UserEditor } from "./UserEditor";

// Formulaire de création d'un utilisateur de la pharmacie.
function CreateUserForm({ form, setForm, roleOptions, submitting, formError, onSubmit }) {
    return (
        <div style={cardStyle}>
            <h2 style={cardTitleStyle}>Ajouter un utilisateur</h2>
            <form onSubmit={onSubmit} noValidate>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "16px" }}>
                    <div style={fieldStyle}>
                        <FieldLabel htmlFor="admin-user-first-name">Prénom</FieldLabel>
                        <input id="admin-user-first-name" style={inputStyle} placeholder="Camille" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
                    </div>
                    <div style={fieldStyle}>
                        <FieldLabel htmlFor="admin-user-last-name">Nom</FieldLabel>
                        <input id="admin-user-last-name" style={inputStyle} placeholder="Lefèvre" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
                    </div>
                    <div style={fieldStyle}>
                        <FieldLabel htmlFor="admin-user-email" required>Email</FieldLabel>
                        <input id="admin-user-email" style={inputStyle} type="email" placeholder="prenom@pharma.fr" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
                    </div>
                    <div style={fieldStyle}>
                        <FieldLabel htmlFor="admin-user-password" required>Mot de passe</FieldLabel>
                        <input id="admin-user-password" style={inputStyle} type="password" placeholder="••••••••" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required />
                        <PasswordConditions password={form.password} />
                    </div>
                </div>
                <div style={{ display: "flex", gap: "16px", alignItems: "flex-end" }}>
                    <div style={{ ...fieldStyle, width: "200px" }}>
                        <FieldLabel htmlFor="admin-user-role" required>Rôle</FieldLabel>
                        <select id="admin-user-role" style={inputStyle} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                            {roleOptions.map(r => <option key={r} value={r}>{ROLE_STYLES[r]?.label ?? r}</option>)}
                        </select>
                    </div>
                    <button type="submit" disabled={submitting} style={btnBlackStyle}>
                        {submitting ? "Création..." : "Créer l'utilisateur"}
                    </button>
                </div>
                {formError && <p style={errorStyle}>{formError}</p>}
            </form>
        </div>
    );
}

// Ligne d'un utilisateur, suivie de son éditeur quand il est ouvert.
function UserRow({ user: u, index: idx, editOpen, roleOptions, onToggleEdit, onDelete, onSaved, onCloseEditor }) {
    const initials = userInitials(u);
    const { bg, text } = avatarColor(u.email ?? u.id.toString());
    const roleStyle = ROLE_STYLES[u.role];
    const displayName = [u.firstName, u.lastName].filter(Boolean).join(" ");
    return (
        <>
            <tr style={{ borderBottom: editOpen ? "none" : "1px solid #f3f4f6" }}>
                <td style={tdStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{ width: "38px", height: "38px", borderRadius: "50%", background: bg, color: text, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 700, flexShrink: 0 }}>
                            {initials}
                        </div>
                        <div>
                            <div style={{ fontWeight: 600, fontSize: "14px", color: displayName ? "#111827" : "#9ca3af", fontStyle: displayName ? "normal" : "italic" }}>
                                {displayName || "Sans nom"}
                            </div>
                            <div style={{ fontSize: "12px", color: "#9ca3af" }}>#{idx + 1}</div>
                        </div>
                    </div>
                </td>
                <td style={{ ...tdStyle, color: "#374151" }}>{u.email}</td>
                <td style={tdStyle}>
                    {roleStyle ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "3px 10px", borderRadius: "20px", background: roleStyle.bg, fontSize: "13px", fontWeight: 500, color: roleStyle.text }}>
                            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: roleStyle.dot, flexShrink: 0 }} />
                            {roleStyle.label}
                        </span>
                    ) : (
                        <span style={{ fontSize: "13px", color: "#6b7280" }}>{u.role}</span>
                    )}
                </td>
                <td style={{ ...tdStyle, width: "1%" }}>
                    <button
                        onClick={onToggleEdit}
                        style={editOpen ? { ...btnOutlineStyle, background: "#f3f4f6" } : btnOutlineStyle}
                    >
                        {editOpen ? "Fermer" : "Éditer"}
                    </button>
                </td>
                <td style={{ ...tdStyle, width: "1%" }}>
                    <button onClick={onDelete} style={btnOutlineDangerStyle}>Supprimer</button>
                </td>
            </tr>
            {editOpen && (
                <tr>
                    <td colSpan={5} style={{ padding: 0, borderBottom: "1px solid #f3f4f6" }}>
                        <UserEditor
                            user={u}
                            roleOptions={roleOptions.includes(u.role) ? roleOptions : [u.role, ...roleOptions]}
                            onSaved={onSaved}
                            onCancel={onCloseEditor}
                        />
                    </td>
                </tr>
            )}
        </>
    );
}

export function UsersTab() {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ email: "", password: "", role: "PHARMACIEN", firstName: "", lastName: "" });
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const roleOptions = currentUser?.role === "TITULAIRE" || currentUser?.role === "SUPER_ADMIN"
        ? ROLES
        : ROLES.filter(role => role !== "TITULAIRE");

    const fetchUsers = async () => {
        try {
            const { data } = await API.get("/admin/users");
            setUsers(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchUsers(); }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError(null);
        const validationError = validateUserCredentials(form);
        if (validationError) {
            setFormError(validationError);
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await API.post("/admin/users", form, { noRedirectOn401: true });
            setUsers(prev => [...prev, data]);
            setForm({ email: "", password: "", role: "PHARMACIEN", firstName: "", lastName: "" });
        } catch (err) {
            const status = err.response?.status;
            const fallbackMessage = err.response?.data?.message ?? "Erreur lors de la création.";
            setFormError(status === 409 ? "Cet email est déjà utilisé." : fallbackMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!globalThis.confirm("Supprimer cet utilisateur ?")) return;
        try {
            await API.delete(`/admin/users/${id}`);
            setUsers(prev => prev.filter(u => u.id !== id));
        } catch {
            alert("Erreur lors de la suppression.");
        }
    };

    const handleUserSaved = (updated) => {
        setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
        setEditingUser(null);
    };

    return (
        <div>
            {/* ── Formulaire création ──────────────────────────────────── */}
            <CreateUserForm
                form={form}
                setForm={setForm}
                roleOptions={roleOptions}
                submitting={submitting}
                formError={formError}
                onSubmit={handleSubmit}
            />

            {/* ── Liste utilisateurs ───────────────────────────────────── */}
            <div style={cardStyle}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
                    <h2 style={{ ...cardTitleStyle, margin: 0 }}>Utilisateurs</h2>
                    {!loading && (
                        <span style={countBadgeStyle}>{users.length}</span>
                    )}
                </div>
                {loading ? (
                    <p style={{ color: "#9ca3af", fontSize: "14px" }}>Chargement...</p>
                ) : (
                    <table style={tableStyle}>
                        <thead>
                            <tr>
                                <th style={thStyle}>Utilisateur</th>
                                <th style={thStyle}>Email</th>
                                <th style={thStyle}>Rôle</th>
                                <th style={thStyle} />
                                <th style={thStyle} />
                            </tr>
                        </thead>
                        <tbody>
                            {users.length === 0 && (
                                <tr><td colSpan={5} style={{ padding: "20px", textAlign: "center", color: "#9ca3af", fontSize: "14px" }}>Aucun utilisateur</td></tr>
                            )}
                            {users.map((u, idx) => (
                                <UserRow
                                    key={u.id}
                                    user={u}
                                    index={idx}
                                    editOpen={editingUser === u.id}
                                    roleOptions={roleOptions}
                                    onToggleEdit={() => setEditingUser(editingUser === u.id ? null : u.id)}
                                    onDelete={() => handleDelete(u.id)}
                                    onSaved={handleUserSaved}
                                    onCloseEditor={() => setEditingUser(null)}
                                />
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
